import { normalizeChapterOverrides } from "../chapterNumbering";
import { parseZwDoc, serializeZwDoc } from "../zwdoc/format";
import {
  emptyEntityFrontmatter,
  entityFrontmatterToWire,
  normalizeEntityFrontmatter,
  type EntityFrontmatter,
} from "../entityFrontmatter";
import { isMapFileName } from "../mapDocument";
import {
  parseDemoJoinedPath,
  relativeProjectPath,
} from "../platform/projectRoot";
import { joinProjectPath } from "../runtime";
import type { EditorActiveTab, RawEditorPayload } from "../tauri/core";
import type { NoteEntry } from "../tauri/notes";
import type { UnplacedItem } from "../tauri/unplaced";
import { countManuscriptWords } from "../wordCount";
import { newZwDocText } from "../zwdoc/format";
import {
  isLegacyDocumentPath,
  planProjectConversion,
  renameConfigReferences,
  renamedTarget,
  zwdocPathFor,
  type ProjectFile,
} from "../zwdoc/convertProject";
import {
  chapterFolderFromScenePath,
  flattenChapters,
  insertIndexAfterSpan,
  manuscriptFolderExists,
  sceneFileFromScenePath,
  scenePath,
  type ZwChapter,
  type ZwConfig,
  type ZwScene,
} from "../zwConfig";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  mapMaybe,
  matchMaybe,
  nothing,
  type Maybe,
  toMaybe,
} from "../maybe";
import {
  buildDemoFixture,
  type DemoDeletedEntry,
  type DemoHistoryEntry,
} from "./fixture";

export type DemoSession = {
  rootPath: string;
  config: ZwConfig;
  files: Map<string, string>;
  /** Assets/ relative path → raw base64 (no data-URL prefix). */
  assets: Map<string, string>;
  databaseFields: Map<string, EntityFrontmatter>;
  graphLayout: Record<string, { x: number; y: number }>;
  activeRelative: Maybe<string>;
  activeTab: Maybe<EditorActiveTab>;
  history: Map<string, DemoHistoryEntry[]>;
  deleted: DemoDeletedEntry[];
  unplaced: UnplacedItem[];
  nextOid: number;
};

/** 64×64 solid amber PNG for map Assets placement e2e. */
const DEMO_MAP_STAMP_PNG_BASE64 =
  "iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAZUlEQVR42u3QQREAAAQAMHFEFE8zcjh7rMCiK+ezECBAgAABAgQIECBAgAABAgQIECBAgAABAgQIECBAgAABAgQIECBAgAABAgQIECBAgAABAgQIECBAgAABAgQIECBAgAABAu5bRV7yLG3KFScAAAAASUVORK5CYII=";

const DEMO_MAP_STAMP_PATH = "Assets/demo-stamp.png";

function mimeForAssetPath(relativePath: string): string {
  const ext = relativePath.split(".").pop()?.toLowerCase() ?? "png";
  switch (ext) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "avif":
      return "image/avif";
    default:
      return "image/png";
  }
}

function isDemoAssetPath(relativePath: string): boolean {
  const normalized = relativePath.replace(/\\/g, "/");
  return (
    normalized.startsWith("Assets/") &&
    normalized.includes("..") === false &&
    normalized.slice("Assets/".length).includes("/") === false
  );
}

let session: Maybe<DemoSession> = nothing;

function cloneConfig(config: ZwConfig): ZwConfig {
  return structuredClone(config);
}

function argString(key: string, args?: Record<string, unknown>): string {
  const v = args?.[key];
  if (typeof v !== "string") throw new Error(`Missing string arg: ${key}`);
  return v;
}

function argStringOpt(
  key: string,
  args?: Record<string, unknown>,
): Maybe<string> {
  const v = args?.[key];
  if (v === null || typeof v === "undefined") return nothing;
  if (typeof v !== "string") throw new Error(`Invalid string arg: ${key}`);
  return just(v);
}

function argNumber(key: string, args?: Record<string, unknown>): number {
  const v = args?.[key];
  if (typeof v !== "number") throw new Error(`Missing number arg: ${key}`);
  return v;
}

function requireSession(): DemoSession {
  if (isNothing(session)) throw new Error("Demo session is not active");
  return session.value;
}

function activeRelativePath(s: DemoSession): string {
  return isJust(s.activeRelative) ? s.activeRelative.value : "";
}

function isActiveRelative(s: DemoSession, path: string): boolean {
  return isJust(s.activeRelative) && s.activeRelative.value === path;
}

function setActiveRelativePath(s: DemoSession, path: string): void {
  s.activeRelative = just(path);
}

function clearActiveRelative(s: DemoSession): void {
  s.activeRelative = nothing;
}

function updateActiveRelativeIf(
  s: DemoSession,
  pred: (rel: string) => boolean,
  update: (rel: string) => string,
): void {
  if (isJust(s.activeRelative) && pred(s.activeRelative.value)) {
    s.activeRelative = just(update(s.activeRelative.value));
  }
}

export function demoAbs(rel: string): string {
  const s = requireSession();
  if (rel === "") return s.rootPath;
  return joinProjectPath(s.rootPath, rel);
}

function relFromAbs(path: string): string {
  const s = requireSession();
  if (path.startsWith("demo://")) {
    return parseDemoJoinedPath(path).relative;
  }
  return relativeProjectPath(s.rootPath, path);
}

function ensureDocName(name: string, label: string): string {
  const trimmed = name.trim();
  if (trimmed === "") throw new Error(`Enter a ${label} name`);
  if (
    trimmed.includes("/") ||
    trimmed.includes("\\") ||
    trimmed.includes("..")
  ) {
    throw new Error(`Invalid ${label} name`);
  }
  // A name that already carries a document extension is used as-is (a
  // legacy `.md` only exists until the project converts, #259).
  if (/\.(zwdoc|md|markdown|mdown)$/i.test(trimmed)) return trimmed;
  return `${trimmed}.zwdoc`;
}

function ensureMapName(name: string): string {
  const trimmed = name.trim();
  if (trimmed === "") throw new Error("Enter a map name");
  if (
    trimmed.includes("/") ||
    trimmed.includes("\\") ||
    trimmed.includes("..")
  ) {
    throw new Error("Invalid map name");
  }
  return isMapFileName(trimmed) ? trimmed : `${trimmed}.zwmap`;
}

function sanitizeFolder(name: string, label: string): string {
  const trimmed = name.trim();
  if (trimmed === "") throw new Error(`Enter a ${label} name`);
  const sanitized = [...trimmed]
    .map((ch) => {
      if (/[/\\:*?"<>|]/.test(ch)) return "-";
      // Control chars (U+0000–U+001F) aren't valid in folder names.
      return ch.charCodeAt(0) < 32 ? "-" : ch;
    })
    .join("")
    .replace(/^[.\s-]+|[.\s-]+$/g, "");
  if (sanitized === "" || sanitized === "." || sanitized === "..") {
    throw new Error(`That ${label} name isn't usable as a folder name`);
  }
  return sanitized;
}

function defaultSceneFileName(existing: ZwScene[]): string {
  for (;;) {
    const suffix = Math.random().toString(16).slice(2, 7).padEnd(5, "0");
    const candidate = `scene-${suffix}.zwdoc`;
    if (existing.some((s) => s.file === candidate) === false) return candidate;
  }
}

function findChapter(config: ZwConfig, folder: string): ZwChapter {
  const chapter = flattenChapters(config.manuscript).find(
    (c) => c.folder === folder,
  );
  if (typeof chapter === "undefined") throw new Error("Chapter not found");
  return chapter;
}

/** Removes a chapter from wherever it sits and returns it. An act emptied by the
 * removal stays put, matching the desktop backend. */
function detachChapter(config: ZwConfig, folder: string): ZwChapter {
  const topIdx = config.manuscript.findIndex(
    (item) => item.type === "chapter" && item.folder === folder,
  );
  if (topIdx >= 0) {
    const [item] = config.manuscript.splice(topIdx, 1);
    const { type: _type, ...chapter } = item as { type: "chapter" } & ZwChapter;
    return chapter;
  }
  for (const item of config.manuscript) {
    if (item.type !== "act") continue;
    const idx = item.chapters.findIndex((c) => c.folder === folder);
    if (idx >= 0) return item.chapters.splice(idx, 1)[0]!;
  }
  throw new Error("Chapter not found");
}

function findScene(chapter: ZwChapter, sceneFile: string): ZwScene {
  const scene = chapter.scenes.find((s) => s.file === sceneFile);
  if (typeof scene === "undefined") throw new Error("Scene not found");
  return scene;
}

function synopsisForActive(s: DemoSession): string {
  const rel = activeRelativePath(s);
  if (rel === "") return "";
  const folderM = chapterFolderFromScenePath(rel);
  const fileM = sceneFileFromScenePath(rel);
  return matchMaybe(folderM, {
    nothing: () => {
      const parked = s.unplaced.find((u) => {
        if (u.kind === "scene") return u.name === rel.replace(/^Notes\//, "");
        return false;
      });
      return typeof parked !== "undefined" ? parked.synopsis : "";
    },
    just: (folder) =>
      matchMaybe(fileM, {
        nothing: () => "",
        just: (file) => {
          const chapter = flattenChapters(s.config.manuscript).find(
            (c) => c.folder === folder,
          );
          const scene = chapter?.scenes.find((sc) => sc.file === file);
          return scene?.synopsis ?? "";
        },
      }),
  });
}

function pushHistory(
  s: DemoSession,
  relativePath: string,
  message: string,
  before: string,
  after: string,
): void {
  const oid = `demo${s.nextOid++}`;
  const entry: DemoHistoryEntry = {
    oid,
    message,
    time: Math.floor(Date.now() / 1000),
    before,
    after,
  };
  const list = s.history.get(relativePath) ?? [];
  list.unshift(entry);
  s.history.set(relativePath, list);
}

function pushDeleted(
  s: DemoSession,
  relativePath: string,
  content: string,
  message: string,
): void {
  const oid = `demo${s.nextOid++}`;
  s.deleted.unshift({
    relativePath,
    oid,
    message,
    time: Math.floor(Date.now() / 1000),
    content,
  });
}

function setActive(
  s: DemoSession,
  relative: string | Maybe<string>,
  tab?: EditorActiveTab | Maybe<EditorActiveTab>,
): void {
  s.activeRelative = typeof relative === "string" ? just(relative) : relative;
  if (typeof tab !== "undefined") {
    s.activeTab = typeof tab === "string" ? just(tab) : tab;
  }
}

export function buildPayload(): RawEditorPayload {
  const s = requireSession();
  const rel = activeRelativePath(s);
  const content = rel !== "" ? (s.files.get(rel) ?? "") : "";
  return {
    kind: "project",
    rootPath: s.rootPath,
    activePath: rel !== "" ? demoAbs(rel) : null,
    content,
    title: s.config.metadata.title !== "" ? s.config.metadata.title : "Demo",
    project: cloneConfig(s.config),
    synopsis: synopsisForActive(s),
    activeTab: fromMaybe(s.activeTab),
  };
}

function listNotes(s: DemoSession): NoteEntry[] {
  const entries: NoteEntry[] = [];
  const seen = new Set<string>();

  for (const item of s.unplaced) {
    seen.add(item.name);
    entries.push({
      name: item.name,
      title: item.title,
      synopsis: item.synopsis,
      parked: item.kind,
    });
  }

  for (const rel of s.files.keys()) {
    if (rel.startsWith("Notes/") === false) continue;
    const rest = rel.slice("Notes/".length);
    if (rest.includes("/")) continue;
    if (/\.(zwdoc|md|markdown|mdown)$/i.test(rest) === false) continue;
    if (seen.has(rest)) continue;
    entries.push({ name: rest, title: "", synopsis: "" });
  }

  entries.sort((a, b) =>
    a.name.localeCompare(b.name, void 0, { sensitivity: "base" }),
  );
  return entries;
}

function listUnplaced(s: DemoSession): UnplacedItem[] {
  return s.unplaced.map((u) => ({
    ...u,
    scenes: u.scenes.map((sc) => ({ ...sc })),
  }));
}

function listDatabaseNames(s: DemoSession): string[] {
  return [...s.files.keys()]
    .filter(
      (rel) =>
        rel.startsWith("Database/") &&
        rel.slice("Database/".length).includes("/") === false,
    )
    .map((rel) => rel.slice("Database/".length))
    .filter((name) => name.startsWith(".") === false)
    .sort((a, b) => a.localeCompare(b, void 0, { sensitivity: "base" }));
}

function emptyMapContent(): string {
  return JSON.stringify(
    {
      version: 1,
      viewport: { x: 0, y: 0, zoom: 1 },
      grid: { size: 32, visible: true, snap: true },
      nodes: [],
      edges: [],
    },
    null,
    2,
  );
}

/**
 * `zwdoc` (default): the sample project as a converted project, `.zwdoc`
 * files and references. `markdown` (`?legacy=1`): the pre-conversion
 * Markdown project, so opening it runs the real conversion (#259).
 */
export type DemoDocumentFormat = "markdown" | "zwdoc";

const DEMO_DOCUMENT_DIRS = ["Chapters/", "Notes/", "Database/", "Templates/"];

/** The demo's `list_legacy_documents`: Markdown documents in document folders. */
function demoLegacyDocuments(s: DemoSession): {
  documents: ProjectFile[];
  existing: string[];
  zwconfig: null;
  zwgraph: null;
} {
  const documents: ProjectFile[] = [];
  const existing: string[] = [];
  for (const [path, content] of [...s.files.entries()].sort(([a], [b]) =>
    a.localeCompare(b),
  )) {
    if (DEMO_DOCUMENT_DIRS.some((dir) => path.startsWith(dir)) === false) {
      continue;
    }
    if (isLegacyDocumentPath(path)) documents.push({ path, content });
    else if (/\.zwdoc$/i.test(path)) existing.push(path);
  }
  // The demo keeps config and graph layout as objects, renamed in place.
  return { documents, existing, zwconfig: null, zwgraph: null };
}

/** Same rename as the files: keys ending in a legacy document extension. */
function renamedKeys<T>(map: Map<string, T>): Map<string, T> {
  return new Map(
    [...map.entries()].map(([key, value]) => [
      isLegacyDocumentPath(key) ? zwdocPathFor(key) : key,
      value,
    ]),
  );
}

/** Links and arc moments name their target file, renamed with it. */
function renamedEntityTargets(fields: EntityFrontmatter): EntityFrontmatter {
  return {
    ...fields,
    links: fields.links.map((link) => ({
      ...link,
      file: renamedTarget(link.file),
    })),
    excludeFromScene: fields.excludeFromScene.map(renamedTarget),
    sceneLinks: fields.sceneLinks.map((link) => ({
      file: renamedTarget(link.file),
    })),
    arc: fields.arc.map((moment) => ({
      ...moment,
      link: mapMaybe(moment.link, (link) => ({
        file: renamedTarget(link.file),
      })),
    })),
  };
}

/**
 * The demo's `convert_project_documents`: apply the plan to the in-memory
 * project and rename every reference, like `zw_core::conversion` plus the
 * `.zwconfig` / `.zwgraph` / Database rewrites. `record` adds the
 * conversion to each file's history (the boot-time conversion doesn't).
 */
function applyDemoConversion(
  s: DemoSession,
  plan: { writes: ProjectFile[]; removes: string[] },
  record: boolean,
): void {
  for (const path of plan.removes) {
    const before = s.files.get(path) ?? "";
    s.files.delete(path);
    const target = zwdocPathFor(path);
    const write = plan.writes.find((w) => w.path === target);
    if (record && typeof write !== "undefined") {
      const history = s.history.get(path) ?? [];
      s.history.delete(path);
      s.history.set(target, history);
      pushHistory(
        s,
        target,
        "Convert documents to .zwdoc",
        before,
        write.content,
      );
    }
  }
  for (const write of plan.writes) {
    if (write.path === ".zwconfig" || write.path === "Database/.zwgraph")
      continue;
    s.files.set(write.path, write.content);
  }
  s.config = renameConfigReferences(s.config) as ZwConfig;
  s.unplaced = renameConfigReferences(s.unplaced) as UnplacedItem[];
  s.databaseFields = new Map(
    [...renamedKeys(s.databaseFields)].map(([name, fields]) => [
      name,
      renamedEntityTargets(fields),
    ]),
  );
  s.graphLayout = Object.fromEntries(
    renamedKeys(new Map(Object.entries(s.graphLayout))),
  );
  if (record === false) s.history = renamedKeys(s.history);
  s.activeRelative = matchMaybe(s.activeRelative, {
    nothing: () => s.activeRelative,
    just: (rel) => just(isLegacyDocumentPath(rel) ? zwdocPathFor(rel) : rel),
  });
}

/** The sample project as it is after conversion (boot without `?legacy=1`). */
function convertDemoSession(s: DemoSession): void {
  const legacy = demoLegacyDocuments(s);
  const plan = planProjectConversion({
    documents: legacy.documents,
    existing: legacy.existing,
    zwconfig: nothing,
    zwgraph: nothing,
  });
  if (plan.ok === false) {
    throw new Error(
      `Demo conversion failed: ${plan.error.map((f) => `${f.path}: ${f.reason}`).join("; ")}`,
    );
  }
  applyDemoConversion(s, plan.value, false);
}

export async function resetDemoSession(
  format: DemoDocumentFormat = "zwdoc",
): Promise<DemoSession> {
  const seed = await buildDemoFixture();
  const firstScene = flattenChapters(seed.config.manuscript)[0]?.scenes[0];
  const firstRelative =
    typeof firstScene !== "undefined"
      ? scenePath(
          flattenChapters(seed.config.manuscript)[0]!.folder,
          firstScene.file,
        )
      : null;
  session = just({
    rootPath: seed.rootPath,
    config: cloneConfig(seed.config),
    files: new Map(Object.entries(seed.files)),
    assets: new Map([[DEMO_MAP_STAMP_PATH, DEMO_MAP_STAMP_PNG_BASE64]]),
    databaseFields: new Map(Object.entries(seed.databaseFields)),
    graphLayout: { ...seed.graphLayout },
    activeRelative: toMaybe(firstRelative),
    activeTab: just("manuscript"),
    history: new Map(
      Object.entries(seed.history).map(([k, v]) => [
        k,
        v.map((e) => ({ ...e })),
      ]),
    ),
    deleted: seed.deleted.map((d) => ({ ...d })),
    unplaced: seed.unplaced.map((u) => ({
      ...u,
      scenes: u.scenes.map((sc) => ({ ...sc })),
    })),
    nextOid: 10,
  });
  if (format === "zwdoc") convertDemoSession(session.value);
  return session.value;
}

export function getDemoSession(): Maybe<DemoSession> {
  return session;
}

export function isDemoSessionActive(): boolean {
  return isJust(session);
}

function renameDemoIndex(
  s: DemoSession,
  folder: string,
  index: string,
  name: string,
): void {
  const path = `Chapters/${folder}/${index}`;
  const parsed = parseZwDoc(s.files.get(path) ?? newZwDocText());
  if (parsed.ok === false) throw new Error("Invalid chapter index");
  const header = parsed.value.header;
  header.fields = {
    ...header.fields,
    custom: { ...header.fields?.custom, name },
  };
  s.files.set(path, serializeZwDoc(parsed.value));
}

export async function demoInvoke(
  command: string,
  args?: Record<string, unknown>,
): Promise<unknown> {
  switch (command) {
    case "list_legacy_documents":
      return demoLegacyDocuments(requireSession());

    case "convert_project_documents": {
      const s = requireSession();
      const plan = args?.plan as { writes: ProjectFile[]; removes: string[] };
      applyDemoConversion(s, plan, true);
      return buildPayload();
    }

    case "get_editor_payload":
    case "reload_editor_from_disk":
      return buildPayload();

    case "read_project_file": {
      const path = argString("path", args);
      const rel = relFromAbs(path);
      const s = requireSession();
      if (s.files.has(rel) === false) throw new Error("File not found");
      return s.files.get(rel) ?? "";
    }

    case "save_active": {
      const s = requireSession();
      if (isNothing(s.activeRelative)) {
        throw new Error("No active file");
      }
      const rel = s.activeRelative.value;
      const content = argString("content", args);
      const synopsis = typeof args?.synopsis === "string" ? args.synopsis : "";
      const before = s.files.get(rel) ?? "";
      s.files.set(rel, content);
      const folderM = chapterFolderFromScenePath(rel);
      const fileM = sceneFileFromScenePath(rel);
      matchMaybe(folderM, {
        nothing: () => {
          const noteName = rel.startsWith("Notes/")
            ? rel.slice("Notes/".length)
            : "";
          if (noteName !== "" && noteName.includes("/") === false) {
            const parked = s.unplaced.find(
              (u) => u.kind === "scene" && u.name === noteName,
            );
            if (typeof parked !== "undefined") parked.synopsis = synopsis;
          }
        },
        just: (folder) => {
          matchMaybe(fileM, {
            nothing: () => {},
            just: (file) => {
              const chapter = findChapter(s.config, folder);
              findScene(chapter, file).synopsis = synopsis;
            },
          });
        },
      });
      pushHistory(s, rel, "Autosave", before, content);
      return buildPayload();
    }

    case "read_zw_config":
      return cloneConfig(requireSession().config);

    case "write_zw_config": {
      const s = requireSession();
      const config = args?.config;
      if (config === null || typeof config !== "object") {
        throw new Error("Missing config");
      }
      s.config = cloneConfig(config as ZwConfig);
      return cloneConfig(s.config);
    }

    case "create_project_chapter": {
      const s = requireSession();
      const name = argString("name", args).trim();
      const folder = "chapter-" + crypto.randomUUID();
      if (manuscriptFolderExists(s.config.manuscript, folder)) {
        throw new Error("A chapter or act with that name already exists");
      }
      const sceneFile = defaultSceneFileName([]);
      const content = newZwDocText();
      const rel = scenePath(folder, sceneFile);
      s.files.set(rel, content);
      s.config.manuscript.push({
        type: "chapter",
        folder,
        name,
        index: "index.zwdoc",
        scenes: [{ file: sceneFile, synopsis: "" }],
        synopsis: "",
      });
      renameDemoIndex(s, folder, "index.zwdoc", name);
      setActive(s, rel, "manuscript");
      return buildPayload();
    }

    case "create_project_act": {
      const s = requireSession();
      const name = argString("name", args).trim();
      const folder = "act-" + crypto.randomUUID();
      if (manuscriptFolderExists(s.config.manuscript, folder)) {
        throw new Error("A chapter or act with that name already exists");
      }
      const at = insertIndexAfterSpan(
        s.config.manuscript,
        argStringOpt("afterFolder", args),
      );
      s.config.manuscript.splice(at, 0, {
        type: "act",
        folder,
        name,
        index: "index.zwdoc",
        chapters: [],
      });
      // Deliberately no `setActive`: an act has no prose to open, so creating
      // one leaves the editor exactly where it was.
      return buildPayload();
    }

    case "rename_project_act": {
      const s = requireSession();
      const folder = argString("fromFolder", args);
      const act = s.config.manuscript.find(
        (item) => item.type === "act" && item.folder === folder,
      );
      if (typeof act === "undefined" || act.type !== "act")
        throw new Error("Act not found");
      const name = argString("toFolder", args).trim();
      act.name = name;
      return buildPayload();
    }

    case "delete_project_act": {
      const s = requireSession();
      const folder = argString("folder", args);
      const idx = s.config.manuscript.findIndex(
        (item) => item.type === "act" && item.folder === folder,
      );
      if (idx < 0) throw new Error("Act not found");
      const [act] = s.config.manuscript.splice(idx, 1);
      // Promote the act's chapters to top level, in place: they hold the prose,
      // and removing the grouping must never remove the writing.
      if (typeof act !== "undefined" && act.type === "act") {
        s.config.manuscript.splice(
          idx,
          0,
          ...act.chapters.map((c) => ({ type: "chapter" as const, ...c })),
        );
      }
      return buildPayload();
    }

    case "rename_project_chapter": {
      const s = requireSession();
      const chapter = findChapter(s.config, argString("fromFolder", args));
      const name = argString("toFolder", args).trim();
      renameDemoIndex(s, chapter.folder, chapter.index, name);
      chapter.name = name;
      return buildPayload();
    }

    case "delete_project_chapter": {
      const s = requireSession();
      const folder = argString("folder", args);
      const chapter = detachChapter(s.config, folder);
      for (const scene of chapter.scenes) {
        const rel = scenePath(folder, scene.file);
        const content = s.files.get(rel) ?? "";
        s.files.delete(rel);
        pushDeleted(s, rel, content, `Delete scene: ${rel}`);
        s.history.delete(rel);
      }
      if (
        isJust(s.activeRelative) &&
        s.activeRelative.value.startsWith(`Chapters/${folder}/`)
      ) {
        const first = flattenChapters(s.config.manuscript)[0];
        const next = first?.scenes[0];
        if (typeof first !== "undefined" && typeof next !== "undefined") {
          setActive(s, scenePath(first.folder, next.file));
        } else {
          clearActiveRelative(s);
        }
      }
      return buildPayload();
    }

    case "update_chapter_numbering": {
      const s = requireSession();
      const folder = argString("folder", args);
      const chapter = findChapter(s.config, folder);
      const overrides = normalizeChapterOverrides({
        [folder]: args?.numbering,
      });
      s.config.chapterNumberingOverrides = {
        ...s.config.chapterNumberingOverrides,
        ...overrides,
      };
      const path = `Chapters/${folder}/${chapter.index}`;
      const parsed = parseZwDoc(s.files.get(path) ?? newZwDocText());
      if (parsed.ok === false) throw new Error("Invalid chapter index");
      parsed.value.header.fields = {
        ...parsed.value.header.fields,
        custom: {
          ...parsed.value.header.fields?.custom,
          chapterCounted:
            overrides[folder]?.counted === false ? "false" : "true",
          chapterHeading: overrides[folder]?.heading ?? "default",
        },
      };
      s.files.set(path, serializeZwDoc(parsed.value));
      return buildPayload();
    }
    case "update_chapter_synopsis": {
      const s = requireSession();
      findChapter(s.config, argString("folder", args)).synopsis = argString(
        "synopsis",
        args,
      );
      return buildPayload();
    }

    case "create_project_scene": {
      const s = requireSession();
      const folder = argString("folder", args);
      const chapter = findChapter(s.config, folder);
      const sceneFile = defaultSceneFileName(chapter.scenes);
      const content = newZwDocText();
      const rel = scenePath(folder, sceneFile);
      s.files.set(rel, content);
      chapter.scenes.push({ file: sceneFile, synopsis: "" });
      setActive(s, rel, "manuscript");
      return buildPayload();
    }

    case "rename_project_scene": {
      const s = requireSession();
      const folder = argString("folder", args);
      const fromSceneFile = argString("fromSceneFile", args);
      const toSceneFile = ensureDocName(
        argString("toSceneFile", args),
        "scene",
      );
      if (toSceneFile.toLowerCase() === "index.zwdoc") {
        throw new Error("That name is reserved");
      }
      const chapter = findChapter(s.config, folder);
      const scene = findScene(chapter, fromSceneFile);
      if (
        fromSceneFile.toLowerCase() !== toSceneFile.toLowerCase() &&
        chapter.scenes.some((sc) => sc.file === toSceneFile)
      ) {
        throw new Error("A scene with that name already exists");
      }
      const from = scenePath(folder, fromSceneFile);
      const to = scenePath(folder, toSceneFile);
      const content = s.files.get(from) ?? "";
      s.files.delete(from);
      s.files.set(to, content);
      const hist = s.history.get(from);
      if (typeof hist !== "undefined") {
        s.history.delete(from);
        s.history.set(to, hist);
      }
      scene.file = toSceneFile;
      if (isActiveRelative(s, from)) setActiveRelativePath(s, to);
      return buildPayload();
    }

    case "delete_project_scene": {
      const s = requireSession();
      const folder = argString("folder", args);
      const sceneFile = argString("sceneFile", args);
      const chapter = findChapter(s.config, folder);
      if (chapter.scenes.length <= 1) {
        throw new Error("Cannot delete the last scene in a chapter");
      }
      const sceneIdx = chapter.scenes.findIndex((sc) => sc.file === sceneFile);
      if (sceneIdx < 0) throw new Error("Scene not found");
      chapter.scenes.splice(sceneIdx, 1);
      const rel = scenePath(folder, sceneFile);
      const content = s.files.get(rel) ?? "";
      s.files.delete(rel);
      pushDeleted(s, rel, content, `Delete scene: ${rel}`);
      s.history.delete(rel);
      if (isActiveRelative(s, rel)) {
        const next =
          chapter.scenes[Math.min(sceneIdx, chapter.scenes.length - 1)]!;
        setActive(s, scenePath(folder, next.file));
      }
      return buildPayload();
    }

    case "move_project_scene": {
      const s = requireSession();
      const fromFolder = argString("fromFolder", args);
      const sceneFile = argString("sceneFile", args);
      const toFolder = argString("toFolder", args);
      const toSceneFile = matchMaybe(argStringOpt("toSceneFile", args), {
        nothing: () => sceneFile,
        just: (value) => value,
      });
      const fromChapter = findChapter(s.config, fromFolder);
      const toChapter = findChapter(s.config, toFolder);
      const sceneIdx = fromChapter.scenes.findIndex(
        (sc) => sc.file === sceneFile,
      );
      if (sceneIdx < 0) throw new Error("Scene not found");
      if (fromFolder !== toFolder && fromChapter.scenes.length <= 1) {
        throw new Error("Cannot move the last scene out of a chapter");
      }
      const [scene] = fromChapter.scenes.splice(sceneIdx, 1);
      if (typeof scene === "undefined") throw new Error("Scene not found");
      const finalName = ensureDocName(toSceneFile, "scene");
      if (toChapter.scenes.some((sc) => sc.file === finalName)) {
        // Match Tauri: surface a recoverable collision instead of silently
        // renaming. Put the scene back so a failed move leaves config intact.
        fromChapter.scenes.splice(sceneIdx, 0, scene);
        throw new Error(
          `SCENE_NAME_COLLISION: A scene named "${finalName}" already exists in that chapter`,
        );
      }
      scene.file = finalName;
      toChapter.scenes.push(scene);
      const from = scenePath(fromFolder, sceneFile);
      const to = scenePath(toFolder, finalName);
      const content = s.files.get(from) ?? "";
      s.files.delete(from);
      s.files.set(to, content);
      const hist = s.history.get(from);
      if (typeof hist !== "undefined") {
        s.history.delete(from);
        s.history.set(to, hist);
      }
      if (isActiveRelative(s, from)) setActive(s, to);
      return buildPayload();
    }

    case "switch_project_chapter": {
      const s = requireSession();
      const folder = argString("folder", args);
      const sceneFile = argString("sceneFile", args);
      findScene(findChapter(s.config, folder), sceneFile);
      setActive(s, scenePath(folder, sceneFile), "manuscript");
      return buildPayload();
    }

    case "update_scene_synopsis": {
      const s = requireSession();
      const folder = argString("folder", args);
      const sceneFile = argString("sceneFile", args);
      findScene(findChapter(s.config, folder), sceneFile).synopsis = argString(
        "synopsis",
        args,
      );
      return buildPayload();
    }

    case "set_active_tab": {
      const s = requireSession();
      const tab = args?.tab;
      s.activeTab =
        tab === "manuscript" ||
        tab === "storyboard" ||
        tab === "notes" ||
        tab === "database" ||
        tab === "trash"
          ? just(tab)
          : nothing;
      return buildPayload();
    }

    case "clear_active_editor_buffer": {
      const s = requireSession();
      clearActiveRelative(s);
      return null;
    }

    case "list_project_notes":
      return listNotes(requireSession());

    case "create_project_note": {
      const s = requireSession();
      const name = ensureDocName(argString("name", args), "note");
      const rel = `Notes/${name}`;
      if (s.files.has(rel) || s.unplaced.some((u) => u.name === name)) {
        throw new Error("A note with that name already exists");
      }
      const content = newZwDocText();
      s.files.set(rel, content);
      setActive(s, rel, "notes");
      return buildPayload();
    }

    case "open_project_note": {
      const s = requireSession();
      const name = ensureDocName(argString("name", args), "note");
      const rel = `Notes/${name}`;
      if (s.files.has(rel) === false) throw new Error("Note not found");
      setActive(s, rel, "notes");
      return buildPayload();
    }

    case "rename_project_note": {
      const s = requireSession();
      const fromName = ensureDocName(argString("fromName", args), "note");
      const toName = ensureDocName(argString("toName", args), "note");
      const from = `Notes/${fromName}`;
      const to = `Notes/${toName}`;
      if (s.files.has(from) === false) throw new Error("Note not found");
      if (s.files.has(to) && fromName.toLowerCase() !== toName.toLowerCase()) {
        throw new Error("A note with that name already exists");
      }
      const content = s.files.get(from) ?? "";
      s.files.delete(from);
      s.files.set(to, content);
      if (isActiveRelative(s, from)) setActiveRelativePath(s, to);
      return buildPayload();
    }

    case "delete_project_note": {
      const s = requireSession();
      const name = ensureDocName(argString("name", args), "note");
      const rel = `Notes/${name}`;
      if (s.files.has(rel) === false) throw new Error("Note not found");
      const content = s.files.get(rel) ?? "";
      s.files.delete(rel);
      pushDeleted(s, rel, content, `Delete note: ${name}`);
      s.unplaced = s.unplaced.filter((u) => u.name !== name);
      if (isActiveRelative(s, rel)) clearActiveRelative(s);
      return buildPayload();
    }

    case "list_project_database":
      return listDatabaseNames(requireSession());

    case "list_project_database_entries": {
      const s = requireSession();
      return listDatabaseNames(s).map((name) => ({
        name,
        isMap: isMapFileName(name),
        fields: entityFrontmatterToWire(
          s.databaseFields.get(name) ?? emptyEntityFrontmatter(),
        ),
      }));
    }

    case "get_project_database_entry_fields": {
      const s = requireSession();
      const name = argString("name", args);
      return entityFrontmatterToWire(
        s.databaseFields.get(name) ?? emptyEntityFrontmatter(),
      );
    }

    case "update_project_database_entry_fields": {
      const s = requireSession();
      const name = argString("name", args);
      const fields = args?.fields;
      if (fields === null || typeof fields !== "object") {
        throw new Error("Missing fields");
      }
      const normalized = normalizeEntityFrontmatter(fields);
      s.databaseFields.set(name, normalized);
      return entityFrontmatterToWire(normalized);
    }

    case "get_project_graph_layout":
      return { ...requireSession().graphLayout };

    case "set_project_graph_layout": {
      const s = requireSession();
      const layout = args?.layout;
      if (layout === null || typeof layout !== "object") {
        throw new Error("Missing layout");
      }
      s.graphLayout = layout as Record<string, { x: number; y: number }>;
      return null;
    }

    case "create_project_database_entry": {
      const s = requireSession();
      const name = ensureDocName(argString("name", args), "database entry");
      const rel = `Database/${name}`;
      if (s.files.has(rel)) {
        throw new Error("A database entry with that name already exists");
      }
      const content = newZwDocText();
      s.files.set(rel, content);
      s.databaseFields.set(name, emptyEntityFrontmatter());
      setActive(s, rel, "database");
      return buildPayload();
    }

    case "create_project_database_map": {
      const s = requireSession();
      const name = ensureMapName(argString("name", args));
      const rel = `Database/${name}`;
      if (s.files.has(rel)) {
        throw new Error("A map with that name already exists");
      }
      s.files.set(rel, emptyMapContent());
      setActive(s, rel, "database");
      return buildPayload();
    }

    case "open_project_database_entry": {
      const s = requireSession();
      const name = argString("name", args);
      const rel = `Database/${name}`;
      if (s.files.has(rel) === false)
        throw new Error("Database entry not found");
      setActive(s, rel, "database");
      return buildPayload();
    }

    case "rename_project_database_entry": {
      const s = requireSession();
      const fromName = argString("fromName", args);
      const toName = isMapFileName(fromName)
        ? ensureMapName(argString("toName", args))
        : ensureDocName(argString("toName", args), "database entry");
      const from = `Database/${fromName}`;
      const to = `Database/${toName}`;
      if (s.files.has(from) === false)
        throw new Error("Database entry not found");
      if (s.files.has(to) && fromName.toLowerCase() !== toName.toLowerCase()) {
        throw new Error("A database entry with that name already exists");
      }
      s.files.set(to, s.files.get(from) ?? "");
      s.files.delete(from);
      const fields = s.databaseFields.get(fromName);
      if (typeof fields !== "undefined") {
        s.databaseFields.delete(fromName);
        s.databaseFields.set(toName, fields);
      }
      if (typeof s.graphLayout[fromName] !== "undefined") {
        s.graphLayout[toName] = s.graphLayout[fromName]!;
        delete s.graphLayout[fromName];
      }
      if (isActiveRelative(s, from)) setActiveRelativePath(s, to);
      return buildPayload();
    }

    case "delete_project_database_entry": {
      const s = requireSession();
      const name = argString("name", args);
      const rel = `Database/${name}`;
      if (s.files.has(rel) === false)
        throw new Error("Database entry not found");
      const content = s.files.get(rel) ?? "";
      s.files.delete(rel);
      s.databaseFields.delete(name);
      delete s.graphLayout[name];
      pushDeleted(s, rel, content, `Delete database: ${name}`);
      if (isActiveRelative(s, rel)) clearActiveRelative(s);
      return buildPayload();
    }

    case "list_project_unplaced":
      return listUnplaced(requireSession());

    case "park_scene": {
      const s = requireSession();
      const folder = argString("folder", args);
      const sceneFile = argString("sceneFile", args);
      const chapter = findChapter(s.config, folder);
      if (chapter.scenes.length <= 1) {
        throw new Error("Cannot move the last scene in a chapter to draft");
      }
      const sceneIdx = chapter.scenes.findIndex((sc) => sc.file === sceneFile);
      if (sceneIdx < 0) throw new Error("Scene not found");
      const [scene] = chapter.scenes.splice(sceneIdx, 1);
      if (typeof scene === "undefined") throw new Error("Scene not found");
      const from = scenePath(folder, sceneFile);
      let parkName = sceneFile;
      if (
        s.files.has(`Notes/${parkName}`) ||
        s.unplaced.some((u) => u.name === parkName)
      ) {
        parkName = defaultSceneFileName(
          s.unplaced
            .filter((u) => u.kind === "scene")
            .map((u) => ({ file: u.name, synopsis: "" })),
        );
      }
      const content = s.files.get(from) ?? "";
      s.files.delete(from);
      s.files.set(`Notes/${parkName}`, content);
      s.unplaced.push({
        kind: "scene",
        name: parkName,
        title: chapter.name,
        synopsis: scene.synopsis,
        scenes: [],
      });
      if (isActiveRelative(s, from)) {
        const next =
          chapter.scenes[Math.min(sceneIdx, chapter.scenes.length - 1)]!;
        setActive(s, scenePath(folder, next.file));
      }
      return buildPayload();
    }

    case "park_chapter": {
      const s = requireSession();
      const folder = argString("folder", args);
      if (flattenChapters(s.config.manuscript).length <= 1) {
        throw new Error("Cannot move the last chapter to draft");
      }
      const chapter = detachChapter(s.config, folder);
      let parkName = folder;
      if (s.unplaced.some((u) => u.name === parkName)) {
        parkName = `${folder} (draft)`;
      }
      const scenes = chapter.scenes.map((sc) => {
        const from = scenePath(folder, sc.file);
        const content = s.files.get(from) ?? "";
        s.files.delete(from);
        s.files.set(`Notes/${parkName}/${sc.file}`, content);
        return { file: sc.file, synopsis: sc.synopsis };
      });
      s.files.set(
        `Notes/${parkName}/index.zwdoc`,
        s.files.get(`Chapters/${folder}/${chapter.index}`) ?? newZwDocText(),
      );
      s.files.delete(`Chapters/${folder}/${chapter.index}`);
      s.unplaced.push({
        kind: "chapter",
        name: parkName,
        title: chapter.name,
        synopsis: chapter.synopsis,
        scenes,
      });
      if (
        isJust(s.activeRelative) &&
        s.activeRelative.value.startsWith(`Chapters/${folder}/`)
      ) {
        const first = flattenChapters(s.config.manuscript)[0];
        const next = first?.scenes[0];
        if (typeof first !== "undefined" && typeof next !== "undefined") {
          setActive(s, scenePath(first.folder, next.file));
        } else {
          clearActiveRelative(s);
        }
      }
      return buildPayload();
    }

    case "unpark_scene": {
      const s = requireSession();
      const name = argString("name", args);
      const toFolder = argString("toFolder", args);
      const toSceneFile = argStringOpt("toSceneFile", args);
      const uIdx = s.unplaced.findIndex(
        (u) => u.kind === "scene" && u.name === name,
      );
      if (uIdx < 0) throw new Error("Draft scene not found");
      const [parked] = s.unplaced.splice(uIdx, 1);
      if (typeof parked === "undefined")
        throw new Error("Draft scene not found");
      const chapter = findChapter(s.config, toFolder);
      let finalName = matchMaybe(toSceneFile, {
        nothing: () => parked.name,
        just: (value) =>
          value !== "" ? ensureDocName(value, "scene") : parked.name,
      });
      if (chapter.scenes.some((sc) => sc.file === finalName)) {
        finalName = defaultSceneFileName(chapter.scenes);
      }
      const from = `Notes/${name}`;
      const to = scenePath(toFolder, finalName);
      const content = s.files.get(from) ?? "";
      s.files.delete(from);
      s.files.set(to, content);
      chapter.scenes.push({ file: finalName, synopsis: parked.synopsis });
      setActive(s, to, "manuscript");
      return buildPayload();
    }

    case "unpark_chapter": {
      const s = requireSession();
      const name = argString("name", args);
      const atIndex = argNumber("atIndex", args);
      const uIdx = s.unplaced.findIndex(
        (u) => u.kind === "chapter" && u.name === name,
      );
      if (uIdx < 0) throw new Error("Draft chapter not found");
      const [parked] = s.unplaced.splice(uIdx, 1);
      if (typeof parked === "undefined")
        throw new Error("Draft chapter not found");
      let folder = sanitizeFolder(name, "chapter");
      if (manuscriptFolderExists(s.config.manuscript, folder)) {
        folder = sanitizeFolder(`${name} restored`, "chapter");
      }
      const scenes: ZwScene[] = [];
      for (const sc of parked.scenes) {
        const from = `Notes/${name}/${sc.file}`;
        const content = s.files.get(from) ?? "";
        s.files.delete(from);
        s.files.set(scenePath(folder, sc.file), content);
        scenes.push({ file: sc.file, synopsis: sc.synopsis });
      }
      s.files.set(
        `Chapters/${folder}/index.zwdoc`,
        s.files.get(`Notes/${name}/index.zwdoc`) ?? newZwDocText(),
      );
      s.files.delete(`Notes/${name}/index.zwdoc`);
      const chapter: ZwChapter = {
        folder,
        name: parked.title,
        index: "index.zwdoc",
        scenes:
          scenes.length > 0
            ? scenes
            : [{ file: defaultSceneFileName([]), synopsis: "" }],
        synopsis: parked.synopsis,
      };
      if (
        chapter.scenes.length === 1 &&
        s.files.has(scenePath(folder, chapter.scenes[0]!.file)) === false
      ) {
        s.files.set(scenePath(folder, chapter.scenes[0]!.file), newZwDocText());
      }
      // `atIndex` is a position in the flat chapter order (that's all the
      // holding pen thinks in), so walk top-level spans until that many
      // chapters have gone by. A target inside an act lands just before the
      // act rather than in it, matching the desktop backend.
      let seen = 0;
      let insertAt = s.config.manuscript.length;
      for (const [position, item] of s.config.manuscript.entries()) {
        if (seen >= atIndex) {
          insertAt = position;
          break;
        }
        seen += item.type === "act" ? item.chapters.length : 1;
      }
      s.config.manuscript.splice(insertAt, 0, { type: "chapter", ...chapter });
      setActive(s, scenePath(folder, chapter.scenes[0]!.file), "manuscript");
      return buildPayload();
    }

    case "open_parked_item": {
      const s = requireSession();
      const name = argString("name", args);
      const rel = `Notes/${name}`;
      if (s.files.has(rel) === false && name.includes("/") === false) {
        // chapter index
        const chapterRel = `Notes/${name}/index.zwdoc`;
        if (s.files.has(chapterRel)) {
          setActive(s, chapterRel, "notes");
          return buildPayload();
        }
      }
      if (s.files.has(rel) === false) throw new Error("Draft item not found");
      setActive(s, rel, "notes");
      return buildPayload();
    }

    case "update_parked_scene_synopsis": {
      const s = requireSession();
      const name = argString("name", args);
      const synopsis = argString("synopsis", args);
      const item = s.unplaced.find((u) => u.name === name);
      if (typeof item === "undefined") throw new Error("Draft item not found");
      if (item.kind === "scene") {
        item.synopsis = synopsis;
      } else if (name.includes("/")) {
        const [chapterName, sceneFile] = name.split("/");
        const chapter = s.unplaced.find(
          (u) => u.kind === "chapter" && u.name === chapterName,
        );
        const scene = chapter?.scenes.find((sc) => sc.file === sceneFile);
        if (typeof scene !== "undefined") scene.synopsis = synopsis;
      } else {
        item.synopsis = synopsis;
      }
      return null;
    }

    case "rename_parked_item": {
      const s = requireSession();
      const name = argString("name", args);
      const toNameRaw = argString("toName", args);
      const item = s.unplaced.find((u) => u.name === name);
      if (typeof item === "undefined") throw new Error("Draft item not found");
      if (item.kind === "scene") {
        const toName = ensureDocName(toNameRaw, "note");
        if (s.unplaced.some((u) => u.name === toName && u !== item)) {
          throw new Error("A draft item with that name already exists");
        }
        const from = `Notes/${name}`;
        const to = `Notes/${toName}`;
        s.files.set(to, s.files.get(from) ?? "");
        s.files.delete(from);
        item.name = toName;
        if (isActiveRelative(s, from)) setActiveRelativePath(s, to);
      } else {
        const toName = sanitizeFolder(toNameRaw, "chapter");
        if (s.unplaced.some((u) => u.name === toName && u !== item)) {
          throw new Error("A draft item with that name already exists");
        }
        for (const key of [...s.files.keys()]) {
          if (key.startsWith(`Notes/${name}/`)) {
            const rest = key.slice(`Notes/${name}/`.length);
            s.files.set(`Notes/${toName}/${rest}`, s.files.get(key) ?? "");
            s.files.delete(key);
          }
        }
        item.name = toName;
        updateActiveRelativeIf(
          s,
          (rel) => rel.startsWith(`Notes/${name}/`),
          (rel) => rel.replace(`Notes/${name}/`, `Notes/${toName}/`),
        );
      }
      return buildPayload();
    }

    case "delete_parked_item": {
      const s = requireSession();
      const name = argString("name", args);
      const uIdx = s.unplaced.findIndex((u) => u.name === name);
      if (uIdx < 0) throw new Error("Draft item not found");
      const [item] = s.unplaced.splice(uIdx, 1);
      if (typeof item === "undefined") throw new Error("Draft item not found");
      if (item.kind === "scene") {
        const rel = `Notes/${name}`;
        const content = s.files.get(rel) ?? "";
        s.files.delete(rel);
        pushDeleted(s, rel, content, `Delete note: ${name}`);
        if (isActiveRelative(s, rel)) clearActiveRelative(s);
      } else {
        for (const key of [...s.files.keys()]) {
          if (key.startsWith(`Notes/${name}/`)) {
            pushDeleted(s, key, s.files.get(key) ?? "", `Delete note: ${key}`);
            s.files.delete(key);
          }
        }
        if (
          isJust(s.activeRelative) &&
          s.activeRelative.value.startsWith(`Notes/${name}/`)
        ) {
          clearActiveRelative(s);
        }
      }
      return buildPayload();
    }

    case "list_file_history": {
      const s = requireSession();
      const relativePath = argString("relativePath", args);
      const limit = typeof args?.limit === "number" ? args.limit : 50;
      const entries = s.history.get(relativePath) ?? [];
      return entries.slice(0, limit).map(({ oid, message, time }) => ({
        oid,
        message,
        time,
      }));
    }

    case "read_chronicle_entries": {
      requireSession();
      return [];
    }

    case "get_file_change": {
      const s = requireSession();
      const relativePath = argString("relativePath", args);
      const oid = argString("oid", args);
      const entry = (s.history.get(relativePath) ?? []).find(
        (e) => e.oid === oid,
      );
      if (typeof entry === "undefined") throw new Error("Change not found");
      return {
        oid: entry.oid,
        message: entry.message,
        time: entry.time,
        before: entry.before,
        after: entry.after,
      };
    }

    case "list_deleted_files": {
      const s = requireSession();
      const limit = typeof args?.limit === "number" ? args.limit : 100;
      return s.deleted
        .slice(0, limit)
        .map(({ relativePath, oid, message, time }) => ({
          relativePath,
          oid,
          message,
          time,
        }));
    }

    case "preview_deleted_file": {
      const s = requireSession();
      const relativePath = argString("relativePath", args);
      const oid = argString("oid", args);
      const entry = s.deleted.find(
        (d) => d.relativePath === relativePath && d.oid === oid,
      );
      if (typeof entry === "undefined")
        throw new Error("Deleted file not found");
      return entry.content;
    }

    case "restore_deleted_file": {
      const s = requireSession();
      const relativePath = argString("relativePath", args);
      const oid = argString("oid", args);
      const idx = s.deleted.findIndex(
        (d) => d.relativePath === relativePath && d.oid === oid,
      );
      if (idx < 0) throw new Error("Deleted file not found");
      if (s.files.has(relativePath))
        throw new Error("A file with that name already exists");
      const [entry] = s.deleted.splice(idx, 1);
      if (typeof entry === "undefined")
        throw new Error("Deleted file not found");
      s.files.set(entry.relativePath, entry.content);
      if (entry.relativePath.startsWith("Notes/")) {
        const name = entry.relativePath.slice("Notes/".length);
        if (name.includes("/") === false) {
          // restored plain note
        }
      } else if (entry.relativePath.startsWith("Database/")) {
        const name = entry.relativePath.slice("Database/".length);
        if (s.databaseFields.has(name) === false) {
          s.databaseFields.set(name, emptyEntityFrontmatter());
        }
      } else if (entry.relativePath.startsWith("Chapters/")) {
        const folderM = chapterFolderFromScenePath(entry.relativePath);
        const fileM = sceneFileFromScenePath(entry.relativePath);
        matchMaybe(folderM, {
          nothing: () => {},
          just: (folder) => {
            matchMaybe(fileM, {
              nothing: () => {},
              just: (file) => {
                let chapter = flattenChapters(s.config.manuscript).find(
                  (c) => c.folder === folder,
                );
                if (typeof chapter === "undefined") {
                  chapter = {
                    folder,
                    name: "",
                    index: "index.zwdoc",
                    scenes: [],
                    synopsis: "",
                  };
                  s.config.manuscript.push({ type: "chapter", ...chapter });
                }
                if (chapter.scenes.some((sc) => sc.file === file) === false) {
                  chapter.scenes.push({ file, synopsis: "" });
                }
              },
            });
          },
        });
      }
      setActive(s, entry.relativePath);
      return buildPayload();
    }

    case "manuscript_words_at_or_before": {
      const s = requireSession();
      const files = args?.files;
      if (Array.isArray(files) === false) return 0;
      let total = 0;
      for (const f of files) {
        if (typeof f !== "string") continue;
        const content = s.files.get(f) ?? "";
        total += countManuscriptWords(content);
      }
      return total;
    }

    case "list_project_assets": {
      const s = requireSession();
      return [...s.assets.keys()].sort();
    }

    case "remove_project_asset": {
      const s = requireSession();
      const relativePath = argString("relativePath", args);
      if (isDemoAssetPath(relativePath) === false) {
        throw new Error("Invalid asset path");
      }
      s.assets.delete(relativePath);
      return null;
    }

    case "read_project_asset_data_url": {
      const s = requireSession();
      const relativePath = argString("relativePath", args);
      if (isDemoAssetPath(relativePath) === false) {
        throw new Error("Invalid asset path");
      }
      const base64 = s.assets.get(relativePath);
      if (typeof base64 === "undefined") throw new Error("Asset not found");
      return `data:${mimeForAssetPath(relativePath)};base64,${base64}`;
    }

    case "write_project_asset_bytes": {
      const s = requireSession();
      const relativePath = argString("relativePath", args);
      const dataBase64 = argString("dataBase64", args);
      if (isDemoAssetPath(relativePath) === false) {
        throw new Error("Invalid asset path");
      }
      if (dataBase64.length === 0) throw new Error("Empty asset data");
      s.assets.set(relativePath, dataBase64);
      return null;
    }

    default:
      console.warn(`Demo backend: unhandled command ${command}`);
      throw new Error(`Demo backend: unhandled command ${command}`);
  }
}
